/**
 * AdminBundleModal (T8.2) — create/edit bundle.
 *
 * Form fields: name, code (only in create — immutable), multi-select modules,
 * discountPercent. Live preview kalkuluje sum + bundle price w PLN/mies + PLN/rok
 * z `module_pricing` (fetched z `/admin/modules?detail=full` + `/admin/module-pricing`).
 *
 * Backend:
 *   create: POST /admin/bundles {code, name, moduleCodes[], discountPercent}
 *   edit:   PATCH /admin/bundles/:code {name?, moduleCodes?, discountPercent?}
 *
 * Cena pakietu w preview to TYLKO admin sanity-check. Faktyczna cena tenanta jest
 * zamrażana w F4 — patrz banner w AdminBundles + ADR-0012.
 */

import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';
import type { BundleListItem } from '@/pages/AdminBundles';

interface ModuleListItem {
  code: string;
  name: string;
  isActive: boolean;
  gcrecord: string | null;
}

interface ModulePricingRow {
  moduleCode: string;
  currencyCode: string;
  priceMonthly: number;
  priceYearly: number | null;
}

export interface AdminBundleModalProps {
  mode: 'create' | 'edit';
  bundle?: BundleListItem;
  onClose: () => void;
  onSuccess: () => void;
}

const CODE_REGEX = /^[a-z][a-z0-9_]*$/;

function formatPln(grosze: number | null): string {
  if (grosze === null) return '—';
  return (grosze / 100).toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function AdminBundleModal({ mode, bundle, onClose, onSuccess }: AdminBundleModalProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState(bundle?.code ?? '');
  const [name, setName] = useState(bundle?.name ?? '');
  const [moduleCodes, setModuleCodes] = useState<string[]>(bundle?.moduleCodes ?? []);
  const [discountPercent, setDiscountPercent] = useState<number>(bundle?.discountPercent ?? 0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const modulesQuery = useQuery({
    queryKey: ['admin', 'modules', 'list-for-bundle'],
    queryFn: async () => {
      const res = await api.get<{ modules: ModuleListItem[] }>(
        '/admin/modules?detail=full&include_inactive=true&include_deleted=true',
      );
      return res.data.modules;
    },
  });

  const pricingQuery = useQuery({
    queryKey: ['admin', 'module-pricing', 'list'],
    queryFn: async () => {
      const res = await api.get<{ pricing: ModulePricingRow[] }>('/admin/module-pricing');
      return res.data.pricing;
    },
  });

  const pricingByCode = useMemo(() => {
    const m = new Map<string, ModulePricingRow>();
    (pricingQuery.data ?? [])
      .filter((p) => p.currencyCode === 'PLN')
      .forEach((p) => m.set(p.moduleCode, p));
    return m;
  }, [pricingQuery.data]);

  const preview = useMemo(() => {
    let sumMonthly = 0;
    let sumYearly = 0;
    let yearlyComplete = true;
    let plnComplete = true;
    for (const c of moduleCodes) {
      const p = pricingByCode.get(c);
      if (!p) {
        plnComplete = false;
        continue;
      }
      sumMonthly += p.priceMonthly;
      if (p.priceYearly === null) yearlyComplete = false;
      else sumYearly += p.priceYearly;
    }
    const factor = (100 - discountPercent) / 100;
    return {
      sumMonthly: plnComplete ? sumMonthly : null,
      bundleMonthly: plnComplete ? Math.round(sumMonthly * factor) : null,
      sumYearly: plnComplete && yearlyComplete ? sumYearly : null,
      bundleYearly: plnComplete && yearlyComplete ? Math.round(sumYearly * factor) : null,
    };
  }, [moduleCodes, discountPercent, pricingByCode]);

  const codeValid = mode === 'edit' || CODE_REGEX.test(code);
  const nameValid = name.trim().length > 0;
  const modulesValid = moduleCodes.length >= 2;
  const discountValid = discountPercent >= 0 && discountPercent <= 100;
  const formValid = codeValid && nameValid && modulesValid && discountValid;

  // Reset error when fields change
  useEffect(() => {
    setError(null);
  }, [code, name, moduleCodes, discountPercent]);

  const toggleModule = (c: string) => {
    setModuleCodes((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formValid) return;
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'create') {
        await api.post('/admin/bundles', {
          code,
          name: name.trim(),
          moduleCodes,
          discountPercent,
        });
      } else {
        if (!bundle) return;
        const patch: Record<string, unknown> = {};
        if (name.trim() !== (bundle.name ?? '')) patch.name = name.trim();
        if (JSON.stringify(moduleCodes) !== JSON.stringify(bundle.moduleCodes))
          patch.moduleCodes = moduleCodes;
        if (discountPercent !== bundle.discountPercent) patch.discountPercent = discountPercent;
        await api.patch(`/admin/bundles/${bundle.code}`, patch);
      }
      onSuccess();
    } catch (err) {
      const axErr = err as AxiosError<{ error?: string; details?: Record<string, unknown> }>;
      const errorCode = axErr.response?.data?.error ?? null;
      if (errorCode === 'BUNDLE_CODE_TAKEN') {
        setError(t('admin.bundles.modal.errors.codeTaken'));
      } else if (errorCode === 'MODULE_RETIRED') {
        setError(t('admin.bundles.modal.errors.moduleRetired'));
      } else if (errorCode === 'VALIDATION_FAILED') {
        setError(t('admin.bundles.modal.errors.validation'));
      } else {
        setError(t('admin.bundles.modal.errors.generic'));
      }
      setSubmitting(false);
    }
  };

  const moduleOptions = (modulesQuery.data ?? []).filter(
    (m) => !m.gcrecord || moduleCodes.includes(m.code),
  );

  return (
    <Modal
      open
      onClose={() => (submitting ? undefined : onClose())}
      title={t(
        mode === 'create' ? 'admin.bundles.modal.titleCreate' : 'admin.bundles.modal.titleEdit',
      )}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('admin.bundles.modal.cancel')}
          </Button>
          <Button
            type="submit"
            form="bundle-form"
            variant="primary"
            loading={submitting}
            disabled={submitting || !formValid}
          >
            {t('admin.bundles.modal.confirm')}
          </Button>
        </>
      }
    >
      <form id="bundle-form" onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-secondary">
            {t('admin.bundles.modal.codeLabel')}
          </span>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={mode === 'edit' || submitting}
            placeholder="np. starter"
            className="rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 font-mono text-sm text-text-primary disabled:opacity-50"
            data-testid="bundle-modal-code"
          />
          {!codeValid && code.length > 0 && (
            <span className="text-xs text-red-600">{t('admin.bundles.modal.codeInvalid')}</span>
          )}
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-secondary">
            {t('admin.bundles.modal.nameLabel')}
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
            className="rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-primary"
            data-testid="bundle-modal-name"
          />
        </label>

        <fieldset className="flex flex-col gap-2 text-sm">
          <legend className="font-medium text-text-secondary">
            {t('admin.bundles.modal.modulesLabel')}
          </legend>
          {modulesQuery.isLoading && (
            <p className="text-xs text-text-muted">{t('admin.bundles.modal.modulesLoading')}</p>
          )}
          {moduleOptions.map((m) => {
            const p = pricingByCode.get(m.code);
            const isRetired = !!m.gcrecord;
            return (
              <Checkbox
                key={m.code}
                label={
                  <span>
                    <span className="font-mono text-xs">{m.code}</span> — {m.name}
                    {p && (
                      <span className="ml-2 text-xs text-text-muted">
                        {formatPln(p.priceMonthly)} PLN/mies
                      </span>
                    )}
                    {isRetired && (
                      <span className="ml-2 text-xs text-amber-600">
                        {t('admin.bundles.retiredBadge')}
                      </span>
                    )}
                  </span>
                }
                checked={moduleCodes.includes(m.code)}
                onChange={() => toggleModule(m.code)}
                disabled={submitting}
              />
            );
          })}
          {!modulesValid && (
            <span className="text-xs text-red-600">{t('admin.bundles.modal.modulesMin2')}</span>
          )}
        </fieldset>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-secondary">
            {t('admin.bundles.modal.discountLabel')}
          </span>
          <input
            type="number"
            min={0}
            max={100}
            value={discountPercent}
            onChange={(e) => setDiscountPercent(Number(e.target.value))}
            disabled={submitting}
            className="rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-primary"
            data-testid="bundle-modal-discount"
          />
        </label>

        <div
          data-testid="bundle-modal-preview"
          className="rounded-lg border border-border-secondary bg-bg-tertiary px-3 py-2 text-xs text-text-secondary"
        >
          <div>
            {t('admin.bundles.modal.preview.sum')}: {formatPln(preview.sumMonthly)} PLN/mies
          </div>
          <div>
            {t('admin.bundles.modal.preview.bundleMonthly')}: {formatPln(preview.bundleMonthly)}{' '}
            PLN/mies
          </div>
          <div>
            {t('admin.bundles.modal.preview.bundleYearly')}: {formatPln(preview.bundleYearly)}{' '}
            PLN/rok
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
